<template>
  <div class="activity-center-tab-content">
    <activity-center-feature-tabs
      v-model="activeFeature"
      :tabs="featureTabs"
    ></activity-center-feature-tabs>

    <div class="activity-center-tab-content__content">
      <activity-center-feature-section
        v-for="section in featureSections"
        :key="section.name"
        :section="section"
        :focused-notification-id="focusedNotificationId"
        :initially-open="isSectionInitiallyOpen(section)"
        @navigate="$emit('navigate')"
        @retry="$emit('retry')"
        @retry:error="$emit('retry:error')"
      ></activity-center-feature-section>

      <div
        v-if="!featureSections.length"
        class="activity-center-tab-content__empty-state"
      >
        There are currently no jobs running
      </div>
    </div>
  </div>
</template>

<script>
  import { computed } from 'vue'
  import {
    ACTIVITY_CENTER_FEATURE_TABS,
    ACTIVITY_CENTER_FEATURE_TAB_NAMES,
    filterActivitiesByFeature,
    filterActivitiesByStatus,
    getActivityCenterFeatureTabs
  } from '@/constants/activityCenter'
  import ActivityCenterFeatureTabs from './ActivityCenterFeatureTabs.vue'
  import ActivityCenterFeatureSection from './ActivityCenterFeatureSection.vue'

  export default {
    name: 'ActivityCenterTabContent',
    components: {
      ActivityCenterFeatureTabs,
      ActivityCenterFeatureSection
    },
    props: {
      modelValue: {
        type: String,
        required: true
      },
      status: {
        type: String,
        required: true
      },
      activities: {
        type: Array,
        required: true
      },
      focusedNotificationId: {
        type: String,
        default: ''
      },
      openUnreadOnly: {
        type: Boolean,
        default: false
      },
      initiallyOpenSectionKeys: {
        type: Array,
        default: () => []
      }
    },
    emits: ['update:modelValue', 'navigate', 'retry', 'retry:error'],
    setup(props, { emit }) {
      const activeFeature = computed({
        get: () => props.modelValue,
        set: value => emit('update:modelValue', value)
      })

      const featureTabs = computed(() => getActivityCenterFeatureTabs({
        activities: props.activities,
        status: props.status
      }))

      const activitiesByStatus = computed(() => filterActivitiesByStatus(
        props.activities,
        props.status
      ))

      const visibleActivities = computed(() => filterActivitiesByFeature(
        activitiesByStatus.value,
        activeFeature.value
      ))

      const featuresToDisplay = computed(() => {
        const featureTabsWithoutAll = ACTIVITY_CENTER_FEATURE_TABS
          .filter(tab => tab.name !== ACTIVITY_CENTER_FEATURE_TAB_NAMES.ALL)

        if (activeFeature.value === ACTIVITY_CENTER_FEATURE_TAB_NAMES.ALL) {
          return featureTabsWithoutAll
        }

        return featureTabsWithoutAll
          .filter(tab => tab.name === activeFeature.value)
      })

      const featureSections = computed(() => featuresToDisplay.value
        .map(tab => ({
          name: tab.name,
          label: tab.label,
          activities: visibleActivities.value.filter(
            activity => activity.feature === tab.name
          )
        })))

      const isSectionInitiallyOpen = section => {
        if (section.activities.some(
          ({ id }) => id === props.focusedNotificationId
        )) {
          return true
        }

        if (props.openUnreadOnly) {
          const sectionKey = `${ props.status }:${ section.name }`

          return props.initiallyOpenSectionKeys.includes(sectionKey)
        }

        return section.activities.length > 0
      }

      return {
        activeFeature,
        featureTabs,
        featureSections,
        isSectionInitiallyOpen
      }
    }
  }
</script>

<style lang="scss" scoped>
  .activity-center-tab-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: $mi-white;

    &__content {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 18px 24px 32px;
    }

    &__empty-state {
      padding: 32px;
      border: 1px solid $mi-silver-100;
      border-radius: 6px;
      font-family: $mi-typography-font-family-condensed;
      font-size: 14px;
      background-color: $mi-silver-50;
      color: $mi-anthracite-500;
      text-align: center;
    }
  }
</style>
